// B241 (AI_108) — Mapa de rótulo/unidade dos campos de exame laboratorial,
// para a TELA DE CONFERÊNCIA da importação por PDF.
//
// As CHAVES são as mesmas do backend (api/_lib/dicionarioExamesLab.js) e de
// `ExameLab` (camelCase). É o contrato da extração. ⚠️ Manter em sincronia com
// o dicionário do backend ao adicionar/renomear campos.

export interface RotuloCampoLab {
  label: string;
  unidade: string;
}

export const CAMPOS_LAB_ROTULO: Record<string, RotuloCampoLab> = {
  // Hemograma
  hemoglobina: { label: "Hemoglobina", unidade: "g/dL" },
  hematocrito: { label: "Hematócrito", unidade: "%" },
  leucocitos: { label: "Leucócitos", unidade: "/mm³" },
  neutrofilos: { label: "Neutrófilos (absoluto)", unidade: "/mm³" },
  eosinofilos: { label: "Eosinófilos (absoluto)", unidade: "/mm³" },
  linfocitos: { label: "Linfócitos (absoluto)", unidade: "/mm³" },
  bastonetes: { label: "Bastonetes (absoluto)", unidade: "/mm³" },
  plaquetas: { label: "Plaquetas", unidade: "/mm³" },
  // Coagulação
  tap: { label: "TAP (atividade)", unidade: "%" },
  inr: { label: "INR", unidade: "" },
  ttpa: { label: "TTPA", unidade: "seg" },
  fibrinogenio: { label: "Fibrinogênio", unidade: "mg/dL" },
  fatorV: { label: "Fator V", unidade: "%" },
  // Função hepática
  bilirrubinaTotal: { label: "Bilirrubina total", unidade: "mg/dL" },
  bilirrubinaDireta: { label: "Bilirrubina direta", unidade: "mg/dL" },
  bilirrubinaIndireta: { label: "Bilirrubina indireta", unidade: "mg/dL" },
  tgo: { label: "TGO / AST", unidade: "U/L" },
  tgp: { label: "TGP / ALT", unidade: "U/L" },
  fosfataseAlcalina: { label: "Fosfatase alcalina", unidade: "U/L" },
  ggt: { label: "Gama-GT", unidade: "U/L" },
  albumina: { label: "Albumina", unidade: "g/dL" },
  proteinasTotais: { label: "Proteínas totais", unidade: "g/dL" },
  globulina: { label: "Globulina", unidade: "g/dL" },
  ldh: { label: "LDH (desidrogenase láctica)", unidade: "U/L" },
  // Função renal
  creatinina: { label: "Creatinina", unidade: "mg/dL" },
  ureia: { label: "Ureia", unidade: "mg/dL" },
  acidoUrico: { label: "Ácido úrico", unidade: "mg/dL" },
  // Pâncreas
  amilase: { label: "Amilase", unidade: "U/L" },
  lipase: { label: "Lipase", unidade: "U/L" },
  // Eletrólitos
  sodio: { label: "Sódio", unidade: "mEq/L" },
  potassio: { label: "Potássio", unidade: "mEq/L" },
  magnesio: { label: "Magnésio", unidade: "mg/dL" },
  calcio: { label: "Cálcio", unidade: "mg/dL" },
  calcioIonico: { label: "Cálcio iônico", unidade: "mmol/L" },
  fosforo: { label: "Fósforo", unidade: "mg/dL" },
  cloro: { label: "Cloro", unidade: "mEq/L" },
  // Lipídios
  colesterolTotal: { label: "Colesterol total", unidade: "mg/dL" },
  ldl: { label: "LDL", unidade: "mg/dL" },
  hdl: { label: "HDL", unidade: "mg/dL" },
  vldl: { label: "VLDL", unidade: "mg/dL" },
  triglicerides: { label: "Triglicérides", unidade: "mg/dL" },
  // Tireoide
  tsh: { label: "TSH", unidade: "µUI/mL" },
  t4Livre: { label: "T4 livre", unidade: "ng/dL" },
  t3Total: { label: "T3 total", unidade: "ng/dL" },
  // Metabolismo / infecção
  glicemia: { label: "Glicemia", unidade: "mg/dL" },
  hemoglobinaGlicada: { label: "Hemoglobina glicada", unidade: "%" },
  frutosamina: { label: "Frutosamina", unidade: "µmol/L" },
  pcr: { label: "PCR (proteína C reativa)", unidade: "mg/L" },
  procalcitonina: { label: "Procalcitonina", unidade: "ng/mL" },
  lactato: { label: "Lactato", unidade: "mmol/L" },
  // Necrose miocárdica (B342 — o folhão do pós-operatório registra os três)
  cpk: { label: "CPK (creatinoquinase)", unidade: "U/L" },
  ckMb: { label: "CK-MB", unidade: "ng/mL" },
  troponina: { label: "Troponina", unidade: "ng/mL" },
  // Gasometria
  gasometriaPh: { label: "pH (gasometria)", unidade: "" },
  gasometriaPco2: { label: "pCO2", unidade: "mmHg" },
  gasometriaPo2: { label: "pO2", unidade: "mmHg" },
  gasometriaHco3: { label: "HCO3 (bicarbonato)", unidade: "mEq/L" },
  gasometriaBe: { label: "BE (excesso de bases)", unidade: "mEq/L" },
  gasometriaSaturacao: { label: "Saturação O2 (gasometria)", unidade: "%" },
  // Níveis de imunossupressor
  nivelTacrolimus: { label: "Nível de tacrolimo", unidade: "ng/mL" },
  nivelEverolimus: { label: "Nível de everolimo", unidade: "ng/mL" },
  nivelSirolimus: { label: "Nível de sirolimo", unidade: "ng/mL" },
};

export function rotuloCampoLab(campo: string): RotuloCampoLab {
  return CAMPOS_LAB_ROTULO[campo] || { label: campo, unidade: "" };
}

/**
 * Converte um valor (já normalizado pela extração: ponto decimal, sem
 * separador de milhar) em número. Tolera vírgula decimal por segurança.
 * Retorna null se não for um número simples (ex.: "<0.1", "indetectável") —
 * nesses casos o médico edita o valor na tela de conferência.
 *
 * ⚠️ NÃO faz heurística de separador de milhar de propósito: a desambiguação
 * milhar×decimal (ex.: plaquetas "450.000") é feita na EXTRAÇÃO (a IA usa o
 * contexto clínico) e conferida pelo médico contra o texto-fonte do PDF.
 */
export function parseValorLab(valorTexto: string): number | null {
  if (valorTexto == null) return null;
  const s = String(valorTexto).trim().replace(/\s/g, "").replace(",", ".");
  if (s === "") return null;
  if (!/^-?\d+(\.\d+)?$/.test(s)) return null; // qualificadores (<, >, texto) → null
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}
